import { useState } from 'react';
import { Form } from '@customerio/pluma-components/react';
import { billing } from '@/services/billing';
import { ORDWAY_INBOX_PREVIEWS_RAIL } from '@/services/billing/ordway/credits-purchase';
import { INBOX_PREVIEWS_CONFIG as config } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { CreditsPurchasePackPicker, packNounFor } from './credits-purchase-pack-picker';
import { OrdwayPurchaseFormBody } from './ordway-purchase-form-body';
import { useBillingEditGuard } from '../hooks/use-billing-edit-guard';
import { useOrdwayCreditsPurchase, type OrdwayPurchaseAttempt } from '../hooks/use-ordway-credits-purchase';

// The packs the config declares, read once at module scope so the picker and the price lookup cannot
// drift apart. `packs` is optional on the shared config type (the AI and Builder flows price by the
// unit), but Inbox Previews always sets it; the fallback only keeps the type honest, and submit is
// disabled while no pack matches the selection, so an empty list cannot be bought.
const PACKS = config.packs ?? [];

export type InboxPreviewsOrdwayPurchaseFormProps = {
  /** The surface that opened the form, reported with every event it fires. */
  pageName: CreditsPurchasePageName;
  onSuccess: (info: { savedCard: boolean }) => void;
  onCancel: () => void;
  onProcessingChange?: (isProcessing: boolean) => void;
};

/**
 * The Inbox Previews purchase form for invoice-billed (Ordway) accounts: the same fixed packs as the
 * Stripe form, billed on the account's next invoice rather than charged through Stripe.
 *
 * It collects no card details and shows no tax — the endpoint charges the card on file, and Ordway
 * puts the pack on the bill, so there is nothing for the client to price. Everything about running the
 * purchase lives in {@link useOrdwayCreditsPurchase}, which both Ordway forms share.
 *
 * The `Inbox Preview: Add-on Purchased` event is emitted server-side for this rail (with
 * `purchase_channel: "invoice"`), so this form must not send it. The generic `Credits Purchase: *`
 * funnel events still fire, from the shared hook.
 */
export function InboxPreviewsOrdwayPurchaseForm({
  pageName,
  onSuccess,
  onCancel,
  onProcessingChange,
}: InboxPreviewsOrdwayPurchaseFormProps) {
  const editGuard = useBillingEditGuard();
  const [packSize, setPackSize] = useState(config.defaultQuantity);

  const purchase = useOrdwayCreditsPurchase({
    config,
    pageName,
    rail: ORDWAY_INBOX_PREVIEWS_RAIL,
    startPurchase: (size) => billing.createInboxPreviewsOrdwayPurchase(size),
    poll: billing.pollForOrdwayInboxPreviewsPurchaseTask,
    initialQuantity: config.defaultQuantity,
    onSuccess,
    onProcessingChange,
  });

  // Pack pricing is non-linear, so the price comes from the selected pack rather than a unit price.
  // The pack's size *is* the previews bought, which is what the funnel reports as the credit total.
  const selectedPack = PACKS.find((pack) => pack.size === packSize);
  const attempt: OrdwayPurchaseAttempt = {
    quantity: packSize,
    totalPriceCents: selectedPack?.priceCents ?? 0,
    totalCredits: packSize,
  };

  const isSubmitDisabled =
    editGuard.isDisabled || selectedPack == null || purchase.isSubmitting || purchase.isChargeUnverified;
  // An unverified charge outranks the edit guard: it is the one a customer cannot clear here. A
  // missing `selectedPack` gets no copy on purpose — the radios only ever emit a pack size, so it
  // cannot be reached by anything a customer does, and inventing a message would suggest otherwise.
  const submitDisabledMessage =
    purchase.chargeUnverifiedMessage !== '' ? purchase.chargeUnverifiedMessage : editGuard.disabledMessage;

  const handlePackSelect = (size: number) => {
    setPackSize(size);
    const pack = PACKS.find((candidate) => candidate.size === size);
    // Track from the selected pack, not from `attempt` — that still holds the previous selection in
    // this tick.
    purchase.trackQuantityChanged({ quantity: size, totalPriceCents: pack?.priceCents ?? 0, totalCredits: size });
  };

  const runSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }
    await purchase.submit(attempt);
  };

  return (
    <Form autoLoading={false} onSubmit={() => void runSubmit()} data-test-inbox-previews-ordway-purchase-form>
      <OrdwayPurchaseFormBody
        sectionHeading={config.quantitySectionHeading}
        totalPriceCents={attempt.totalPriceCents}
        totalCredits={attempt.totalCredits}
        unitLabel={config.creditsUnitLabel ?? 'previews'}
        disclaimer={config.disclaimer}
        termsLinks={config.termsLinks}
        submitLabel={config.submitLabel ?? 'Buy previews'}
        isSubmitDisabled={isSubmitDisabled}
        submitDisabledMessage={submitDisabledMessage}
        isSubmitting={purchase.isSubmitting}
        onCancel={onCancel}
      >
        <CreditsPurchasePackPicker
          packs={PACKS}
          packNoun={packNounFor(config)}
          unitLabel={config.creditsUnitLabel ?? 'previews'}
          value={packSize}
          isDisabled={purchase.isSubmitting}
          onChange={handlePackSelect}
        />
      </OrdwayPurchaseFormBody>
    </Form>
  );
}
